import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { View, Button } from "@/components/ui";
import { WorkerListItem } from "./worker-list-item";
import type { Worker } from "@/types/booking";
import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "@/store/auth";
import { PackageDepartmentEnum } from "@/__generated__/graphql";
import { Typography } from "../../typography";
import { Spin } from "@/components/shared/spin";
import { IfElse } from "../../if-else";
import AllCleaners from "@/assets/images/booking/all-cleaners.png";
import AllBeautyTechnicians from "@/assets/images/booking/all-beauty-technicians.png";
import { useClientPastWorkersQuery } from "@/gqlhooks/profile/useClientPastWorkersQuery";

export const ChooseWorkerModal = ({
  open,
  workerLabel,
  setOpen,
  department,
  selectedWorkers,
  handleApply,
  visitWorker,
}: {
  open: boolean;
  workerLabel: string;
  setOpen(open: boolean): void;
  department: PackageDepartmentEnum;
  selectedWorkers: Worker[];
  handleApply(workers: Worker[]): void;
  visitWorker?: Worker | null;
}) => {
  const {
    data: { userInfo },
  } = useAuthState();
  const { workers: pastWorkers, loading } = useClientPastWorkersQuery({
    skip: !open || !userInfo.id,
    variables: {
      filters: {
        clientId: userInfo.id,
        department,
      },
    },
  });

  const allWorkers = useMemo(() => {
    if (!visitWorker && !pastWorkers?.length) return [];

    return [
      ...(visitWorker ? [visitWorker] : []),
      ...(pastWorkers?.filter((worker) => worker.id !== visitWorker?.id) || []),
    ];
  }, [visitWorker, pastWorkers]);

  const [currentSelectedWorkers, setCurrentSelectedWorkers] =
    useState(selectedWorkers);

  const resetWorkers = () => {
    setCurrentSelectedWorkers(selectedWorkers);
  };

  useEffect(() => {
    if (open) resetWorkers();
  }, [open]);

  const onSelectAll = () => {
    setCurrentSelectedWorkers([]);
  };

  const onSelectAllPastWorker = () => {
    setCurrentSelectedWorkers(allWorkers);
  };

  const onSelectItem = (worker: Worker) => {
    const isChecked = currentSelectedWorkers.some((w) => w.id === worker.id);
    if (isChecked) {
      setCurrentSelectedWorkers(
        currentSelectedWorkers.filter((w) => w.id !== worker.id),
      );
    } else {
      setCurrentSelectedWorkers([...currentSelectedWorkers, worker]);
    }
  };

  const onApply = () => {
    handleApply(currentSelectedWorkers);
    setOpen(false);
  };

  return (
    <BottomDrawerModal open={open} onOpenChange={setOpen}>
      <Typography variant="label-sm">
        {`Filter availabilities by ${workerLabel.toLocaleLowerCase()}`}
      </Typography>
      <View className="divide-y">
        <WorkerListItem
          name={`All ${workerLabel.toLocaleLowerCase()}`}
          onCheck={onSelectAll}
          checked={!currentSelectedWorkers.length}
          avatarUrl={
            department === PackageDepartmentEnum.HomeCleaning
              ? AllCleaners
              : AllBeautyTechnicians
          }
          avatarClassName="border-info-border border"
        />
        <View>
          <View className="flex flex-row items-center justify-between py-2">
            <Typography color="foreground-intermediate" variant="body-sm">
              {`Or Select any of your past ${workerLabel.toLocaleLowerCase()}`}
            </Typography>
            <Typography
              className="cursor-pointer text-primary"
              onClick={onSelectAllPastWorker}
              disabled={loading}
              variant="body-sm"
            >
              Select All
            </Typography>
          </View>
          <View className="divide-y">
            {loading ? (
              <Spin className="my-6 size-8" />
            ) : (
              <IfElse
                if={!!allWorkers.length}
                else={
                  <Typography
                    variant="caption-md"
                    className="my-4"
                    align="center"
                  >
                    {`No ${workerLabel.toLocaleLowerCase()}`}
                  </Typography>
                }
              >
                {allWorkers.map((worker) => (
                  <WorkerListItem
                    key={worker.id}
                    name={worker.fullName}
                    rating={worker.workerRating}
                    avatarUrl={worker.avatarUrl ?? ""}
                    checked={currentSelectedWorkers.some(
                      (w) => w.id === worker.id,
                    )}
                    onCheck={() => {
                      onSelectItem(worker);
                    }}
                  />
                ))}
              </IfElse>
            )}
          </View>
        </View>
      </View>

      <View className="flex flex-row gap-1 md:gap-2">
        <Button
          variant="secondary"
          color="CTA2"
          className="flex-1"
          onClick={() => {
            resetWorkers();
            setOpen(false);
          }}
          children="Cancel"
        />
        <Button
          variant="primary"
          color="CTA"
          className="flex-1"
          onClick={onApply}
          children="Apply"
        />
      </View>
    </BottomDrawerModal>
  );
};
